import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import StatusStamp from '../components/StatusStamp';
import WorkflowPipeline from '../components/WorkflowPipeline';
import Modal from '../components/Modal';
import DocumentViewer from '../components/DocumentViewer';
import SignatureModal from '../components/SignatureModal';
import useSignature from '../hooks/useSignature';
import { formatDate } from '../utils/date';

export default function DocumentDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showSubmitReviewModal, setShowSubmitReviewModal] = useState(false);
  const [reviewerIds, setReviewerIds] = useState([]);
  const [approverEntries, setApproverEntries] = useState([{ user_id: '', approval_role_id: '' }]);
  const [rejectionReason, setRejectionReason] = useState('');
  const [reviewComments, setReviewComments] = useState('');
  const [users, setUsers] = useState([]);
  const [approvalRoles, setApprovalRoles] = useState([]);
  const [showNewVersionModal, setShowNewVersionModal] = useState(false);
  const [newVersionLog, setNewVersionLog] = useState('');
  const [newVersionFile, setNewVersionFile] = useState(null);
  const [showAuthorModal, setShowAuthorModal] = useState(false);
  const [newAuthorId, setNewAuthorId] = useState('');
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approvalComments, setApprovalComments] = useState('');
  // Versoes do historico com o painel de comentarios aberto
  const [expandedVersions, setExpandedVersions] = useState([]);
  const [copias, setCopias] = useState(null);
  const signature = useSignature();

  useEffect(() => { loadDocument(); }, [id]);

  async function loadDocument() {
    setLoading(true);
    try { const { data } = await api.get(`/documents/${id}`); setDoc(data); }
    catch { setError('Documento não encontrado'); }
    setLoading(false);
  }
  // /users e exclusiva de Administrador; o autor que envia para revisao
  // costuma ser Padrao. /users/options atende qualquer autenticado.
  async function loadUsers() {
    try {
      const { data } = await api.get('/users/options', { params: { limit: 200 } });
      setUsers(data.data);
    } catch {
      setUsers([]);
      setError('Não foi possível carregar a lista de pessoas.');
    }
  }
  async function loadApprovalRoles() { try { const { data } = await api.get('/approval-roles'); setApprovalRoles(data.data); } catch {} }

  async function handleSendToReview() {
    setError(''); setSuccess('');
    try { await api.post(`/documents/${id}/send-to-review`, { reviewer_ids: reviewerIds }); setSuccess('Documento enviado para revisão'); setShowReviewModal(false); loadDocument(); }
    catch (err) { setError(err.response?.data?.error || 'Erro'); }
  }
  async function handleSubmitReview() {
    setError(''); setSuccess('');
    try {
      await signature.run(
        async () => {
          await api.post(`/documents/${id}/submit-review`, { comments: reviewComments });
          setSuccess('Revisão concluída');
          setShowSubmitReviewModal(false);
          loadDocument();
        },
        {
          title: 'Assinar revisão',
          description: `Confirme sua identidade para registrar a conclusão da revisão de ${doc?.code}. `
            + 'A assinatura fica na auditoria com seu nome, o método usado e a data.',
        }
      );
    } catch (err) { setError(err.response?.data?.error || 'Erro'); }
  }
  async function handleSendToApproval() {
    setError(''); setSuccess('');
    const approvers = approverEntries.filter((a) => a.user_id).map((a) => ({ user_id: a.user_id, approval_role_id: a.approval_role_id || undefined }));
    try { await api.post(`/documents/${id}/send-to-approval`, { approvers }); setSuccess('Documento enviado para aprovação'); setShowApprovalModal(false); loadDocument(); }
    catch (err) { setError(err.response?.data?.error || 'Erro'); }
  }
  async function handleApprove() {
    setError(''); setSuccess('');
    try {
      await signature.run(
        async () => {
          await api.post(`/documents/${id}/approve`, { comments: approvalComments });
          setSuccess('Documento aprovado');
          setShowApproveModal(false);
          loadDocument();
        },
        {
          title: 'Assinar aprovação',
          description: `Confirme sua identidade para aprovar ${doc?.code}. `
            + 'A aprovação é definitiva e fica registrada com seu nome, o método usado e a data.',
        }
      );
    } catch (err) { setError(err.response?.data?.error || 'Erro'); }
  }
  async function handleCreateVersion() {
    setError(''); setSuccess('');
    if (!newVersionLog.trim()) { setError('Descreva o motivo da revisão'); return; }
    try {
      const formData = new FormData();
      formData.append('change_log', newVersionLog);
      if (newVersionFile) formData.append('file', newVersionFile);
      const { data } = await api.post(`/documents/${id}/new-version`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess(`Versão ${data.version_number}.${data.sub_version} criada. Edite e envie para revisão.`);
      setShowNewVersionModal(false);
      setNewVersionLog(''); setNewVersionFile(null);
      loadDocument();
    } catch (err) { setError(err.response?.data?.error || 'Erro ao criar nova versão'); }
  }

  async function handleChangeAuthor() {
    setError(''); setSuccess('');
    try {
      const { data } = await api.put(`/documents/${id}/author`, { user_id: newAuthorId });
      setSuccess(`Autoria transferida para ${data.author.name}`);
      setShowAuthorModal(false);
      setNewAuthorId('');
      loadDocument();
    } catch (err) { setError(err.response?.data?.error || 'Erro ao trocar autor'); }
  }

  async function handleReject() {
    setError(''); setSuccess('');
    try { await api.post(`/documents/${id}/reject`, { rejection_reason: rejectionReason }); setSuccess('Documento rejeitado'); setShowRejectModal(false); loadDocument(); }
    catch (err) { setError(err.response?.data?.error || 'Erro'); }
  }
  // Abre a versao carimbada em outra guia, onde o leitor de PDF do navegador
  // oferece o dialogo de impressao. Nao usamos o iframe da tela porque o PDF
  // exibido ali e a copia limpa, sem marca d'agua.
  function handlePrint() {
    window.open(`/api/documents/${id}/print`, '_blank', 'noopener');
  }

  // Arquivo como foi enviado: sem conversao, sem folha, sem marca d'agua.
  // Serve para o autor editar e subir a proxima versao.
  async function handleDownloadOriginal(v) {
    setError('');
    const rota = v ? `/documents/${id}/original/${v.id}` : `/documents/${id}/original`;
    try {
      const response = await api.get(rota, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement('a');
      link.href = url;
      // O servidor manda o nome no Content-Disposition, mas o axios nao o
      // aplica sozinho: reaproveitamos o file_name que ja veio no documento.
      link.download = (v || doc.version)?.file_name || doc.code;
      link.click();
      window.URL.revokeObjectURL(url);
      if (copias) loadCopias();
    } catch (err) {
      let msg = 'Erro ao baixar o arquivo original';
      try { msg = JSON.parse(await err.response.data.text()).error || msg; } catch {}
      setError(msg);
    }
  }

  async function loadCopias() {
    try {
      const { data } = await api.get(`/documents/${id}/copies`, { params: { page: 1, limit: 50 } });
      setCopias(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Nao foi possivel carregar as copias emitidas');
    }
  }

  // `v` ausente = versao exibida. O servidor sempre devolve PDF (com folha de
  // aprovacao quando aprovada, e sempre com marca d'agua), entao o nome do
  // arquivo segue o codigo e a versao — nao o file_name original, que pode
  // ser .docx e enganaria o sistema operacional.
  async function handleDownload(v) {
    setError('');
    const alvo = v || doc.version;
    const rota = v ? `/documents/${id}/download/${v.id}` : `/documents/${id}/download`;
    try {
      const response = await api.get(rota, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = window.document.createElement('a');
      link.href = url;
      link.download = alvo
        ? `${doc.code}-v${alvo.version_number}.${alvo.sub_version}.pdf`
        : `${doc.code}.pdf`;
      link.click();
      // Sem revoke, cada download segura o blob em memoria ate sair da pagina
      window.URL.revokeObjectURL(url);
      // Cada download emite uma copia nova; se o painel esta aberto, atualiza
      if (copias) loadCopias();
    } catch (err) {
      // responseType blob faz o corpo do erro chegar como Blob, nao como JSON
      let msg = 'Erro ao fazer download';
      try { msg = JSON.parse(await err.response.data.text()).error || msg; } catch {}
      setError(msg);
    }
  }

  if (loading) return <div className="flex items-center justify-center py-12 text-ink-muted">Carregando...</div>;
  if (!doc) return <div className="p-3 rounded-md text-sm bg-status-rejected/10 text-status-rejected border border-status-rejected/20">{error}</div>;

  const version = doc.version;
  const isAuthor = doc.created_by === user?.id;
  const isEditable = version && ['draft', 'rejected'].includes(version.status);
  const isReviewer = doc.reviewers?.find((r) => r.user_id === user?.id);
  const isApprover = doc.approvers?.find((a) => a.user_id === user?.id);
  const pendingReviewer = isReviewer && isReviewer.status === 'pending';
  const pendingApprover = isApprover && isApprover.status === 'pending';
  const allReviewsDone = doc.reviewers?.length > 0 && doc.reviewers.every((r) => r.status === 'reviewed');

  return (
    <div>
      {error && <div className="p-3 rounded-md text-sm mb-4 bg-status-rejected/10 text-status-rejected border border-status-rejected/20">{error}</div>}
      {success && <div className="p-3 rounded-md text-sm mb-4 bg-status-approved/10 text-status-approved border border-status-approved/20">{success}</div>}

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <span className="doc-code">{doc.code}</span>
            {version && <StatusStamp status={version.status} />}
          </div>
          <h1 className="text-xl font-bold tracking-tight">{doc.name}</h1>
          {version && <span className="version-label mt-1 inline-block">Versao {version.version_number}.{version.sub_version}</span>}
        </div>
        <div className="flex gap-2">
          {/* O servidor decide quem pode baixar e imprimir; aqui so refletimos */}
          {doc.can_print && <button className="btn btn-sm btn-secondary" onClick={handlePrint}>Imprimir</button>}
          {doc.can_download && <button className="btn btn-sm btn-secondary" onClick={() => handleDownload()}>Download</button>}
          {/* Rotulo distingue as duas saidas: uma e a copia controlada
              (PDF carimbado), a outra e o arquivo de trabalho. */}
          {doc.can_download_original && (
            <button className="btn btn-sm btn-secondary" onClick={() => handleDownloadOriginal()}
              title="Arquivo como foi enviado, sem conversão e sem marca d'água. Para editar e criar a próxima versão.">
              Baixar original
            </button>
          )}
          {isAuthor && isEditable && <button className="btn btn-sm btn-secondary" onClick={() => navigate(`/documents/${id}/edit`)}>Editar</button>}
          {doc.can_create_version && (
            <button className="btn btn-sm btn-primary" onClick={() => { setNewVersionLog(''); setNewVersionFile(null); setShowNewVersionModal(true); }}>
              Nova Versão
            </button>
          )}
        </div>
      </div>

      {/* Workflow Pipeline + Actions */}
      {version && (
        <div className="bg-surface-100 border rounded-md p-4 mb-4">
          <WorkflowPipeline currentStatus={version.status} />
          <div className="mt-4 flex gap-2">
            {isAuthor && isEditable && (
              <button className="btn btn-sm btn-primary" onClick={() => { loadUsers(); setShowReviewModal(true); }}>Enviar para Revisão</button>
            )}
            {pendingReviewer && version.status === 'in_review' && (
              <button className="btn btn-sm btn-primary" onClick={() => setShowSubmitReviewModal(true)}>Concluir Revisão</button>
            )}
            {isAuthor && version.status === 'in_review' && allReviewsDone && (
              <button className="btn btn-sm btn-primary" onClick={() => { loadUsers(); loadApprovalRoles(); setShowApprovalModal(true); }}>Enviar para Aprovação</button>
            )}
            {pendingApprover && version.status === 'in_approval' && (
              <>
                <button className="btn btn-sm btn-primary" onClick={() => { setApprovalComments(''); setShowApproveModal(true); }}>Aprovar</button>
                <button className="btn btn-sm btn-danger" onClick={() => setShowRejectModal(true)}>Rejeitar</button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4">
        <div className="space-y-4">
          {/* Visualizacao dentro do sistema */}
          {version && (
            doc.can_preview ? (
              <DocumentViewer
                documentId={id}
                versionId={version.id}
                canDownload={doc.can_download}
                onDownload={() => handleDownload()}
              />
            ) : (
              <div className="bg-surface-100 border rounded-md p-6 text-center">
                <div className="text-sm text-ink-secondary">
                  O formato .{version.file_type} nao tem visualizacao no sistema.
                </div>
                {doc.can_download && (
                  <button className="btn btn-sm btn-secondary mt-3" onClick={() => handleDownload()}>
                    Baixar arquivo
                  </button>
                )}
              </div>
            )
          )}

          {/* Description */}
          {doc.description && (
            <div className="bg-surface-100 border rounded-md p-4">
              <h3 className="text-base font-semibold mb-2">Descrição</h3>
              <p className="text-sm text-ink-secondary">{doc.description}</p>
            </div>
          )}

          {/* Reviewers */}
          {doc.reviewers?.length > 0 && (
            <div className="bg-surface-100 border rounded-md p-4">
              <h3 className="text-base font-semibold mb-3">Revisores</h3>
              {doc.reviewers.map((r) => (
                <div key={r.id} className="flex justify-between items-center py-2 border-b border-subtle">
                  <div>
                    <div className="text-sm font-medium">{r.user_name}</div>
                    <div className="text-xs text-ink-muted">{r.user_email}</div>
                  </div>
                  <StatusStamp status={r.status === 'reviewed' ? 'approved' : 'draft'} />
                </div>
              ))}
              {doc.reviewers.filter((r) => r.comments).map((r) => (
                <div key={`c-${r.id}`} className="mt-3 p-3 bg-surface-inset rounded">
                  <div className="text-xs font-semibold text-ink-muted">{r.user_name}:</div>
                  <div className="text-sm mt-1">{r.comments}</div>
                </div>
              ))}
            </div>
          )}

          {/* Approvers */}
          {doc.approvers?.length > 0 && (
            <div className="bg-surface-100 border rounded-md p-4">
              <h3 className="text-base font-semibold mb-3">Aprovadores</h3>
              {doc.approvers.map((a) => (
                <div key={a.id} className="flex justify-between items-center py-2 border-b border-subtle">
                  <div>
                    <div className="text-sm font-medium">{a.user_name}</div>
                    <div className="text-xs text-ink-muted">{a.role_name || 'Sem função'} — {a.user_email}</div>
                  </div>
                  <StatusStamp status={a.status === 'approved' ? 'approved' : a.status === 'rejected' ? 'rejected' : 'draft'} />
                </div>
              ))}
              {doc.approvers
                .map((a) => ({ ...a, texto: a.status === 'rejected' ? a.rejection_reason : a.comments }))
                .filter((a) => a.texto)
                .map((a) => (
                  <div key={`c-${a.id}`} className="mt-3 p-3 bg-surface-inset rounded">
                    <div className="text-xs font-semibold text-ink-muted">
                      {a.user_name}{a.status === 'rejected' ? ' (rejeitou)' : ''}:
                    </div>
                    <div className="text-sm mt-1 whitespace-pre-wrap">{a.texto}</div>
                  </div>
                ))}
            </div>
          )}

          {/* Version History */}
          {doc.versions?.length > 0 && (
            <div className="bg-surface-100 border rounded-md p-4">
              <h3 className="text-base font-semibold mb-3">Histórico de Versões</h3>
              <table className="w-full border-collapse">
                <thead><tr className="border-b">
                  <th className="text-left text-2xs font-semibold text-ink-muted uppercase px-3 py-2">Versão</th>
                  <th className="text-left text-2xs font-semibold text-ink-muted uppercase px-3 py-2">Status</th>
                  <th className="text-left text-2xs font-semibold text-ink-muted uppercase px-3 py-2">Data</th>
                  <th className="text-left text-2xs font-semibold text-ink-muted uppercase px-3 py-2">Log</th>
                  <th className="text-right text-2xs font-semibold text-ink-muted uppercase px-3 py-2">Ações</th>
                </tr></thead>
                <tbody>
                  {doc.versions.map((v) => {
                    // Todo revisor e todo aprovador entram, com ou sem texto.
                    // Filtrar por comentario escondia quem aprovou sem
                    // escrever nada — que e a maioria, e e justamente a
                    // evidencia de quem assinou.
                    const parecer = [
                      ...(v.reviewers || []).map((r) => ({
                        ...r,
                        papel: 'Revisor',
                        decisao: r.status === 'reviewed' ? 'Revisou' : 'Pendente',
                        texto: r.comments,
                        quando: r.reviewed_at,
                      })),
                      ...(v.approvers || []).map((a) => ({
                        ...a,
                        papel: a.role_name ? `Aprovador — ${a.role_name}` : 'Aprovador',
                        decisao: a.status === 'approved' ? 'Aprovou'
                          : a.status === 'rejected' ? 'Rejeitou' : 'Pendente',
                        // Aprovador comenta ao aprovar e justifica ao rejeitar:
                        // os dois textos ocupam o mesmo lugar no historico.
                        texto: a.status === 'rejected' ? a.rejection_reason : a.comments,
                        quando: a.decided_at,
                      })),
                    ];
                    const aberta = expandedVersions.includes(v.id);

                    return (
                      <React.Fragment key={v.id}>
                        <tr className="border-b border-subtle">
                          <td className="px-3 py-2"><span className="version-label">{v.version_number}.{v.sub_version}</span></td>
                          <td className="px-3 py-2">
                            <StatusStamp status={v.status} />
                            {v.unavailable_at && (
                              <span className="block text-2xs text-ink-muted mt-0.5" title={`Arquivada em ${formatDate(v.unavailable_at)}`}>
                                indisponivel
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs text-ink-muted">{formatDate(v.created_at)}</td>
                          <td className="px-3 py-2 text-xs">{v.change_log || '—'}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            {parecer.length > 0 && (
                              <button
                                className="text-xs text-brand hover:underline"
                                onClick={() => setExpandedVersions((atual) => (
                                  atual.includes(v.id) ? atual.filter((x) => x !== v.id) : [...atual, v.id]
                                ))}
                              >
                                {aberta ? 'ocultar' : `pareceres (${parecer.length})`}

                              </button>
                            )}
                            {/* Mesma regra do servidor: autor, Manutencao e
                                Administrador baixam qualquer versao — menos as
                                arquivadas pela retencao, cujo conteudo o
                                servidor recusa com 410. */}
                            {doc.can_download && !v.unavailable_at && (
                              <button className="text-xs text-brand hover:underline ml-3" onClick={() => handleDownload(v)}>
                                baixar
                              </button>
                            )}
                            {doc.can_download_original && !v.unavailable_at && (
                              <button className="text-xs text-brand hover:underline ml-3"
                                title="Arquivo como foi enviado, sem carimbo"
                                onClick={() => handleDownloadOriginal(v)}>
                                original
                              </button>
                            )}
                          </td>
                        </tr>
                        {aberta && (
                          <tr className="border-b border-subtle">
                            <td colSpan={5} className="px-3 pb-3 pt-0">
                              <div className="space-y-2">
                                {parecer.map((p, i) => (
                                  <div key={i} className="p-3 bg-surface-inset rounded">
                                    <div className="flex justify-between gap-2">
                                      <span className="text-xs font-semibold">{p.user_name}</span>
                                      <span className="text-2xs text-ink-muted">{p.quando ? formatDate(p.quando) : ''}</span>
                                    </div>
                                    <div className="text-2xs text-ink-muted">{p.papel} · {p.decisao}</div>
                                    {p.texto
                                      ? <div className="text-sm mt-1 whitespace-pre-wrap">{p.texto}</div>
                                      : <div className="text-xs mt-1 text-ink-muted italic">Sem comentario</div>}
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-2xs text-ink-muted mt-3">
                Aprovar cria uma versao nova a partir da versao de trabalho. Os pareceres de
                revisao e aprovacao aparecem na versao aprovada que eles originaram, nao na
                versao de trabalho substituida.
              </p>
            </div>
          )}

          {/* Rastreabilidade: quem extraiu copias deste documento.
              Mesma permissao do download — o servidor confere de novo. */}
          {doc.can_download && (
            <div className="bg-surface-100 border rounded-md p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold">Copias emitidas</h3>
                {copias === null && (
                  <button className="text-xs text-brand hover:underline" onClick={loadCopias}>
                    carregar
                  </button>
                )}
              </div>

              {copias === null ? (
                <p className="text-xs text-ink-muted">
                  Cada download e impressao carimba um codigo unico no rodape do PDF. A partir
                  de uma copia encontrada fora do sistema, o codigo diz de qual extracao ela
                  saiu.
                </p>
              ) : copias.data.length === 0 ? (
                <p className="text-xs text-ink-muted">Nenhuma copia foi extraida deste documento.</p>
              ) : (
                <>
                  <table className="w-full border-collapse">
                    <thead><tr className="border-b">
                      <th className="text-left text-2xs font-semibold text-ink-muted uppercase px-2 py-2">Codigo</th>
                      <th className="text-left text-2xs font-semibold text-ink-muted uppercase px-2 py-2">Versao</th>
                      <th className="text-left text-2xs font-semibold text-ink-muted uppercase px-2 py-2">Via</th>
                      <th className="text-left text-2xs font-semibold text-ink-muted uppercase px-2 py-2">Quem</th>
                      <th className="text-left text-2xs font-semibold text-ink-muted uppercase px-2 py-2">Quando</th>
                    </tr></thead>
                    <tbody>
                      {copias.data.map((c) => (
                        <tr key={c.code} className="border-b border-subtle">
                          <td className="px-2 py-2 text-xs font-mono">{c.code}</td>
                          <td className="px-2 py-2 text-xs">{c.versao}</td>
                          <td className="px-2 py-2 text-xs">
                            {c.via === 'print' ? 'impressao'
                              : c.via === 'original'
                                // Sem marca d'agua e sem codigo no rodape: e a
                                // extracao que nao se identifica sozinha depois.
                                ? <span className="text-status-pending font-medium">original sem marca</span>
                                : 'download'}
                          </td>
                          <td className="px-2 py-2 text-xs" title={c.email}>{c.para || '—'}</td>
                          <td className="px-2 py-2 text-xs text-ink-muted">{formatDate(c.emitida_em)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-2xs text-ink-muted mt-3">
                    Mostrando {copias.data.length} de {copias.total}. A pagina publica de
                    verificacao confirma origem e situacao, mas nunca revela quem extraiu —
                    esta lista e interna.
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        {/* Details Sidebar */}
        <div className="space-y-4">
          <div className="bg-surface-100 border rounded-md p-4">
            <h3 className="text-base font-semibold mb-3">Detalhes</h3>
            {[
              ['Autor', doc.author?.name], ['Pasta', doc.folder?.name],
              ['Departamento', doc.department?.name], ['Categoria', doc.category?.name],
              ['Processo', doc.process?.name],
              ['Validade', doc.validity_type === 'none' ? 'Sem validade' : `${doc.validity_value} ${doc.validity_type === 'months' ? 'meses' : 'anos'}`],
              ['Acesso Restrito', doc.is_restricted ? 'Sim' : 'Não'],
              ['Impressão', doc.allow_printing ? 'Permitida' : 'Não permitida'],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between py-2 border-b border-subtle gap-2">
                <span className="text-xs text-ink-muted flex-shrink-0">{label}</span>
                <span className="text-sm font-medium text-right">
                  {value}
                  {label === 'Autor' && doc.can_change_author && (
                    <button
                      className="block ml-auto text-xs font-normal text-brand hover:underline mt-0.5"
                      onClick={() => { loadUsers(); setNewAuthorId(''); setShowAuthorModal(true); }}
                    >
                      trocar
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>

          {doc.keywords && (
            <div className="bg-surface-100 border rounded-md p-4">
              <h3 className="text-base font-semibold mb-2">Palavras-chave</h3>
              <div className="flex flex-wrap gap-1">
                {doc.keywords.split(',').map((kw, i) => <span key={i} className="doc-code">{kw.trim()}</span>)}
              </div>
            </div>
          )}

          {doc.access_groups?.length > 0 && (
            <div className="bg-surface-100 border rounded-md p-4">
              <h3 className="text-base font-semibold mb-2">Grupos de Acesso</h3>
              {doc.access_groups.map((g) => <div key={g.id} className="text-sm py-1">{g.name}</div>)}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showReviewModal && (
        <Modal title="Enviar para Revisão" onClose={() => setShowReviewModal(false)}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-ink-secondary mb-1">Selecione os revisores</label>
            <select className="form-select min-h-[120px]" multiple value={reviewerIds}
              onChange={(e) => setReviewerIds(Array.from(e.target.selectedOptions, (o) => o.value))}>
              {users.filter((u) => u.id !== user?.id).map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
            </select>
          </div>
          <div className="flex gap-3 justify-end mt-5">
            <button className="btn btn-secondary" onClick={() => setShowReviewModal(false)}>Cancelar</button>
            <button className="btn btn-primary" onClick={handleSendToReview} disabled={reviewerIds.length === 0}>Enviar</button>
          </div>
        </Modal>
      )}

      {showSubmitReviewModal && (
        <Modal title="Concluir Revisão" onClose={() => setShowSubmitReviewModal(false)}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-ink-secondary mb-1">Comentarios (opcional)</label>
            <textarea className="form-textarea" value={reviewComments} onChange={(e) => setReviewComments(e.target.value)} maxLength={5000} rows={4} />
          </div>
          <div className="flex gap-3 justify-end mt-5">
            <button className="btn btn-secondary" onClick={() => setShowSubmitReviewModal(false)}>Cancelar</button>
            <button className="btn btn-primary" onClick={handleSubmitReview}>Concluir Revisão</button>
          </div>
        </Modal>
      )}

      {showApprovalModal && (
        <Modal title="Enviar para Aprovação" onClose={() => setShowApprovalModal(false)}>
          {approverEntries.map((entry, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <select className="form-select" value={entry.user_id}
                onChange={(e) => { const u = [...approverEntries]; u[i].user_id = e.target.value; setApproverEntries(u); }}>
                <option value="">Aprovador...</option>
                {users.filter((u) => u.id !== user?.id).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <select className="form-select" value={entry.approval_role_id}
                onChange={(e) => { const u = [...approverEntries]; u[i].approval_role_id = e.target.value; setApproverEntries(u); }}>
                <option value="">Função (opcional)</option>
                {approvalRoles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          ))}
          <button className="btn btn-sm btn-secondary mb-3"
            onClick={() => setApproverEntries([...approverEntries, { user_id: '', approval_role_id: '' }])}>+ Adicionar Aprovador</button>
          <div className="flex gap-3 justify-end mt-5">
            <button className="btn btn-secondary" onClick={() => setShowApprovalModal(false)}>Cancelar</button>
            <button className="btn btn-primary" onClick={handleSendToApproval} disabled={!approverEntries.some((a) => a.user_id)}>Enviar</button>
          </div>
        </Modal>
      )}

      {showApproveModal && (
        <Modal title="Aprovar Documento" onClose={() => setShowApproveModal(false)}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-ink-secondary mb-1">Comentarios (opcional)</label>
            <textarea className="form-textarea" value={approvalComments}
              onChange={(e) => setApprovalComments(e.target.value)} maxLength={5000} rows={4} />
            <p className="text-2xs text-ink-muted mt-1">
              Fica registrado junto da sua assinatura, na versao aprovada, e aparece no
              historico de versoes. A aprovacao e definitiva.
            </p>
          </div>
          <div className="flex gap-3 justify-end mt-5">
            <button className="btn btn-secondary" onClick={() => setShowApproveModal(false)}>Cancelar</button>
            <button className="btn btn-primary" onClick={handleApprove}>Assinar e Aprovar</button>
          </div>
        </Modal>
      )}

      {showRejectModal && (
        <Modal title="Rejeitar Documento" onClose={() => setShowRejectModal(false)}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-ink-secondary mb-1">Motivo da rejeicao *</label>
            <textarea className="form-textarea" value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} maxLength={2000} rows={4} required />
          </div>
          <div className="flex gap-3 justify-end mt-5">
            <button className="btn btn-secondary" onClick={() => setShowRejectModal(false)}>Cancelar</button>
            <button className="btn btn-danger" onClick={handleReject} disabled={!rejectionReason.trim()}>Rejeitar</button>
          </div>
        </Modal>
      )}

      {showNewVersionModal && (
        <Modal title="Nova versão para revisão" onClose={() => setShowNewVersionModal(false)}>
          <p className="text-sm text-ink-secondary mb-4">
            Abre a versao {version?.version_number}.{(version?.sub_version || 0) + 1} a partir da
            aprovada {version?.version_number}.{version?.sub_version}. A versao aprovada continua
            sendo a que todos leem ate a nova ser aprovada.
          </p>

          <div className="mb-4">
            <label className="block text-sm font-medium text-ink-secondary mb-1">Motivo da revisão *</label>
            <textarea className="form-textarea" value={newVersionLog} rows={3} maxLength={2000}
              onChange={(e) => setNewVersionLog(e.target.value)}
              placeholder="O que precisa mudar nesta revisão" />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-ink-secondary mb-1">
              Arquivo (opcional)
            </label>
            <div className="text-xs text-ink-muted mb-2">
              Sem arquivo novo, a versao comeca com o conteudo aprovado atual
              ({version?.file_name}) e voce pode substituir depois pela tela de edicao.
            </div>
            <input type="file" className="form-input" onChange={(e) => setNewVersionFile(e.target.files[0] || null)} />
          </div>

          <div className="flex gap-3 justify-end">
            <button className="btn btn-secondary" onClick={() => setShowNewVersionModal(false)}>Cancelar</button>
            <button className="btn btn-primary" onClick={handleCreateVersion} disabled={!newVersionLog.trim()}>
              Criar versão
            </button>
          </div>
        </Modal>
      )}

      {showAuthorModal && (
        <Modal title="Trocar autor do documento" onClose={() => setShowAuthorModal(false)}>
          <p className="text-sm text-ink-secondary mb-4">
            Autor atual: <strong>{doc.author?.name}</strong>. Transferir a autoria muda quem pode
            editar, baixar e criar nova versão, e para quem vao as notificações do fluxo.
          </p>

          <div className="mb-4">
            <label className="block text-sm font-medium text-ink-secondary mb-1">Novo autor *</label>
            <select className="form-select" value={newAuthorId} onChange={(e) => setNewAuthorId(e.target.value)}>
              <option value="">Selecione...</option>
              {users.filter((u) => u.id !== doc.created_by).map((u) => (
                <option key={u.id} value={u.id}>{u.name} — {u.email}</option>
              ))}
            </select>
          </div>

          <div className="flex gap-3 justify-end">
            <button className="btn btn-secondary" onClick={() => setShowAuthorModal(false)}>Cancelar</button>
            <button className="btn btn-primary" onClick={handleChangeAuthor} disabled={!newAuthorId}>
              Transferir autoria
            </button>
          </div>
        </Modal>
      )}

      {signature.pending && (
        <SignatureModal
          title={signature.pending.title}
          description={signature.pending.description}
          onClose={signature.cancel}
          onConfirmed={signature.confirmed}
        />
      )}
    </div>
  );
}
